import styled from '@emotion/styled';
import {Container, media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {FooterLink, Small} from 'UI/components/layout/Text';

import {FooterColumn} from 'UI/modules/LachenschmidLayout/FooterColumn';

import {useFooter} from './useFooter';

import type {Footer as FooterType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The footer configuration data containing content and structure information for both columns of the footer.
     * This data defines the layout, content, and styling properties that will be rendered in the footer section.
     * The footer data is typically retrieved from the CMS and contains all necessary information to display the footer content.
     */
    footerData: FooterType;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Footer'
     */
    testId?: string;
}

/**
 * The Footer component renders the website's footer section with a two-column layout structure.
 * It utilizes the provided footer data to display content in both columns and ensures responsive design through the Container and spacing system.
 * The component is wrapped with MobX observer to enable reactive updates when the footer data changes.
 *
 * @param props            The component props.
 * @param props.footerData The footer configuration data containing content for both columns.
 * @param props.testId     The test identifier for component testing purposes.
 * @returns The footer component JSX with two-column layout.
 *
 * @example
 * ```tsx
 * <Footer
 *     footerData={footerConfiguration}
 *     testId="main-footer"
 * />
 * ```
 */
const Footer = observer(({footerData, testId = 'Footer'}: ComponentProps) => {
    const {
        contentCol,
        contentCol2,
        copyrightText,
        copyrightYear,
        dataProtectionLabel,
        dataProtectionLink,
        dataProtectionRel,
        dataProtectionTarget,
        impressumLabel,
        impressumLink,
        impressumRel,
        impressumTarget,
        machineListLabel,
        machineListLink,
        machineListRel,
        machineListTarget
    } = useFooter(footerData);

    return (
        <StyledFooter as="footer">
            <Wrapper data-cy={testId}>
                <FooterColumn column={contentCol} />
                <SpacerStyled>
                    <Spacer isNotStretching={['xs', 'sm', 'md']} x={0} y={{lg: 0, xs: 8}} />
                </SpacerStyled>
                <FooterColumn column={contentCol2} />
            </Wrapper>
            <Spacer y={22} />
            <CopyrightLine>
                <StyledSmall>© {copyrightYear} {copyrightText}</StyledSmall>
                <LinkWrapper>
                    <FooterLink
                        $size="small"
                        href={machineListLink}
                        rel={machineListRel}
                        target={machineListTarget}
                    >
                        {machineListLabel}
                    </FooterLink>
                    <FooterLink
                        $size="small"
                        href={dataProtectionLink}
                        rel={dataProtectionRel}
                        target={dataProtectionTarget}
                    >
                        {dataProtectionLabel}
                    </FooterLink>
                    <FooterLink
                        $size="small"
                        href={impressumLink}
                        rel={impressumRel}
                        target={impressumTarget}
                    >
                        {impressumLabel}
                    </FooterLink>
                </LinkWrapper>
            </CopyrightLine>
        </StyledFooter>
    );
});

Footer.displayName = 'Footer';

export {Footer};

const StyledFooter = styled(Container)`
    padding-block-end: ${spacing(12)};
`;

const Wrapper = styled.div`
    border-top: 1px solid ${({theme}) => theme.colors.darkBorder};
    display: grid;
    flex: 0 0 fit-content;
    grid-auto-rows: auto;
    grid-template-columns: 1fr auto 1fr;
    padding-block-start: ${spacing(12)};

    ${media('sm')} {
        grid-template-columns: 1fr auto 1fr;
    }

    ${media('lg')} {
        grid-template-columns: 1fr auto auto 0.5fr 1fr auto auto;
    }
`;

const SpacerStyled = styled.div`
    grid-column: 2 / 3;
    grid-row: 4 / 5;

    ${media('sm')} {
        grid-column: inherit;
        grid-row: inherit;
    }
`;

const CopyrightLine = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    justify-content: space-between;

    ${media('lg')} {
        flex-direction: row;
    }
`;

const StyledSmall = styled(Small)`
    text-align: center;

    ${media('lg')} {
        text-align: left;
    }
`;

const LinkWrapper = styled.div`
    align-items: center;
    column-gap: ${spacing(8)};
    display: flex;
    flex-wrap: wrap;
`;